import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bot,
  Eye,
  History,
  Loader2,
  RotateCcw,
  Server,
  TriangleAlert,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  workflowsApi,
  type WorkflowVersion,
  type WorkflowVersionRow,
} from "@/api/workflows";
import { queryKeys } from "@/lib/query-keys";
import { useTenant } from "@/context/tenant-context";
import { cn } from "@/lib/utils";

/**
 * Version history for a workflow, with restore.
 *
 * Exists because nodes/edges are overwritten wholesale on every save — by a
 * human on the canvas or by an agent calling update_workflow — and there was
 * previously no way back.
 *
 * Restoring is not destructive: the current state was itself snapshotted on
 * its way in, and the restore records a new version, so nothing is lost by
 * trying one. The confirm step exists because a restore replaces what's on
 * the canvas right now, which is startling if unintended — not because it's
 * irreversible.
 */
export function VersionHistoryDialog({
  workflowId,
  onClose,
  onRestored,
  onPreview,
  previewingVersionId,
}: {
  workflowId: string;
  onClose: () => void;
  onRestored: () => void;
  /** The version already open on the canvas, so the list can mark it. */
  previewingVersionId?: string | null;
  /**
   * Show this version on the canvas without writing anything. Looking at an
   * old graph and committing to it are different decisions, and the list
   * alone — node counts and a one-line summary — is not enough to make the
   * second one.
   */
  onPreview: (version: WorkflowVersion) => void;
}) {
  const queryClient = useQueryClient();
  // History is readable by everyone; restoring needs agent admin or above on
  // the server, so standard users get View only (2026-09-30).
  const { canManageAnyAgent: canRestore } = useTenant();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const preview = useMutation({
    mutationFn: (versionId: string) =>
      workflowsApi.getVersion(workflowId, versionId),
    onSuccess: (version) => {
      setLoadingId(null);
      onPreview(version);
      onClose();
    },
    onError: (err: unknown) => {
      setLoadingId(null);
      setError(err instanceof Error ? err.message : "Could not open that version");
    },
  });

  const {
    data: versions,
    isLoading,
    isError,
    error: listError,
  } = useQuery({
    queryKey: queryKeys.workflows.versions(workflowId),
    queryFn: () => workflowsApi.listVersions(workflowId),
  });

  const restore = useMutation({
    mutationFn: (versionId: string) =>
      workflowsApi.restoreVersion(workflowId, versionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.detail(workflowId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.versions(workflowId),
      });
      onRestored();
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Could not restore");
    },
  });

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-[min(720px,92vw)] overflow-y-auto rounded-xl border border-border bg-background p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center gap-2">
          <History size={16} className="text-accent" />
          <h2 className="text-[15px] font-semibold">Version history</h2>
        </div>
        <p className="mb-4 text-xs text-text-muted">
          Every save is snapshotted — from the canvas and from agents.
          Restoring writes an old version back and records that as a new
          version, so nothing is lost either way.
        </p>

        {versions && versions.length > 0 && <CreatedAndEdited versions={versions} />}

        {isLoading ? (
          <Loader2 size={18} className="animate-spin text-text-muted" />
        ) : isError ? (
          /* A failed request used to render as "No history yet", so a broken
             endpoint looked exactly like a workflow nobody had saved twice.
             Never let those two say the same thing. */
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 px-3 py-6 text-center">
            <p className="text-sm font-medium text-text-primary">
              History could not be loaded
            </p>
            <p className="mt-1 break-words text-xs text-text-muted">
              {listError instanceof Error
                ? listError.message
                : "The server did not return the version list."}
            </p>
            <p className="mt-2 text-xs text-text-muted">
              This is not the same as having no history — nothing has been lost.
            </p>
          </div>
        ) : !versions || versions.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-surface/50 px-3 py-8 text-center">
            <p className="text-sm font-medium text-text-primary">
              No history yet
            </p>
            <p className="mt-1 text-xs text-text-muted">
              The next save will create the first version. History only covers
              changes made after this feature shipped.
            </p>
          </div>
        ) : (
          <ol className="space-y-2">
            {versions.map((v, i) => (
              <VersionRow
                key={v.id}
                version={v}
                isCurrent={i === 0}
                isViewing={previewingVersionId === v.id}
                isConfirming={confirmId === v.id}
                isPending={restore.isPending && confirmId === v.id}
                isOpening={loadingId === v.id}
                onView={() => {
                  setError(null);
                  setLoadingId(v.id);
                  preview.mutate(v.id);
                }}
                onAskRestore={() => {
                  setError(null);
                  setConfirmId(v.id);
                }}
                onCancelRestore={() => setConfirmId(null)}
                onConfirmRestore={() => restore.mutate(v.id)}
                canRestore={canRestore}
              />
            ))}
          </ol>
        )}

        {error && (
          <p className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function VersionRow({
  version,
  isCurrent,
  isViewing,
  isConfirming,
  isPending,
  isOpening,
  onView,
  onAskRestore,
  onCancelRestore,
  onConfirmRestore,
  canRestore,
}: {
  version: WorkflowVersionRow;
  isCurrent: boolean;
  isViewing: boolean;
  isConfirming: boolean;
  isPending: boolean;
  isOpening: boolean;
  onView: () => void;
  onAskRestore: () => void;
  onCancelRestore: () => void;
  onConfirmRestore: () => void;
  /** False for standard users: they get View only. */
  canRestore: boolean;
}) {
  const author = authorPresentation(version);
  // Prompt loss is the destructive-rewrite signature this feature exists to
  // catch. It gets a badge, not a border: a highlighted row should mean
  // "this is where you are", and having two kinds of highlight at once made
  // the list read as though two rows were both somehow selected.
  const destructive = version.summary?.includes("cleared") ?? false;

  return (
    <li
      className={cn(
        "rounded-lg border bg-surface p-3",
        isViewing
          ? "border-amber-500/50"
          : isCurrent
            ? "border-accent/40"
            : "border-border",
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
            author.tint,
          )}
          title={author.title}
        >
          <author.Icon size={12} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-mono text-[11px] text-text-muted">
              v{version.version}
            </span>
            <span className="truncate text-[13px] font-medium text-text-primary">
              {version.name}
            </span>
            {isCurrent && (
              <span className="rounded bg-accent/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-accent">
                current
              </span>
            )}
            {isViewing && (
              <span className="flex items-center gap-1 rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300">
                <Eye size={9} />
                viewing
              </span>
            )}
            {destructive && (
              <span className="flex items-center gap-1 rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300">
                <TriangleAlert size={9} />
                prompts lost
              </span>
            )}
          </div>

          {/* The date leads, and is the brightest thing on the line. It is
              what someone scans for when they know roughly when a change
              went in but not what it was called. */}
          <p className="mt-1 text-[11px] text-text-muted">
            <span className="font-medium text-text-secondary">
              {formatWhen(version.createdAt)}
            </span>
            <span className="text-text-muted"> · {relativeWhen(version.createdAt)}</span>
          </p>
          <p className="mt-0.5 text-[11px] text-text-muted">
            {author.label} · {version.nodeCount} nodes, {version.edgeCount} edges
          </p>
          {version.summary && (
            <p className="mt-1 text-xs text-text-secondary">{version.summary}</p>
          )}
        </div>

        {!isCurrent && (
          <div className="flex shrink-0 items-center gap-1.5">
            {!isConfirming && !isViewing && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onView}
                disabled={isOpening}
                className="gap-1.5"
                title={`Show v${version.version} on the canvas without changing anything`}
              >
                {isOpening ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <Eye size={11} />
                )}
                View
              </Button>
            )}
            {isConfirming ? (
              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onCancelRestore}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={onConfirmRestore}
                  disabled={isPending}
                  className="gap-1.5"
                >
                  {isPending && <Loader2 size={11} className="animate-spin" />}
                  Confirm
                </Button>
              </div>
            ) : canRestore ? (
              <Button
                size="sm"
                variant="outline"
                onClick={onAskRestore}
                className="gap-1.5"
                title={`Replace the current graph with v${version.version}`}
              >
                <RotateCcw size={11} />
                Restore
              </Button>
            ) : null}
          </div>
        )}
      </div>

      {isConfirming && (
        <p className="mt-2 rounded-md border border-border bg-background px-2.5 py-2 text-[11px] text-text-secondary">
          This replaces the graph on the canvas with v{version.version}. The
          current state is already saved as a version, so you can restore back.
        </p>
      )}
    </li>
  );
}

/**
 * "22 Sept 2026, 18:29". Day and month spelled rather than numeric, because
 * 09/10 reads as two different dates either side of the Atlantic and this list
 * is the thing someone scans when they half-remember when a change went in.
 */
function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "2 days ago". Sits beside the exact date, never instead of it. */
function relativeWhen(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.round(hours / 24);
  if (days < 31) return `${days} ${days === 1 ? "day" : "days"} ago`;
  const months = Math.round(days / 30);
  return `${months} ${months === 1 ? "month" : "months"} ago`;
}

/**
 * "Created by X · Last edited by Y", read from the oldest and newest history
 * entries. The server writes a "Created" entry when a workflow is made (must
 * match WORKFLOW_CREATED_SUMMARY in server/src/services/workflow-versions.ts);
 * workflows made before that, or whose first entry has aged out, say so
 * rather than naming whoever happened to save first.
 */
function CreatedAndEdited({ versions }: { versions: WorkflowVersionRow[] }) {
  const newest = versions[0]!;
  const oldest = versions[versions.length - 1]!;
  const hasCreated = oldest.summary === "Created";
  const editedSince = versions.length > 1 || !hasCreated;
  return (
    <p className="mb-4 flex flex-wrap gap-x-1.5 text-xs text-text-secondary">
      <span>
        {hasCreated ? (
          <>
            Created by <span className="font-medium text-text-primary">{authorPresentation(oldest).label}</span>
            {" · "}
            <span title={formatWhen(oldest.createdAt)}>{relativeWhen(oldest.createdAt)}</span>
          </>
        ) : (
          "Created before version history began"
        )}
      </span>
      <span className="text-text-muted">|</span>
      <span>
        {editedSince ? (
          <>
            Last edited by <span className="font-medium text-text-primary">{authorPresentation(newest).label}</span>
            {" · "}
            <span title={formatWhen(newest.createdAt)}>{relativeWhen(newest.createdAt)}</span>
          </>
        ) : (
          "Not edited since"
        )}
      </span>
    </p>
  );
}

/** Icon, tint and label for who made a change. */
function authorPresentation(version: WorkflowVersionRow) {
  if (version.authorType === "agent") {
    return {
      Icon: Bot,
      tint: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
      title: "Changed by an agent",
      label: version.authorLabel
        ? `${version.authorLabel} (agent)`
        : "Agent",
    };
  }
  if (version.authorType === "system") {
    return {
      Icon: Server,
      tint: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-400",
      title: "Recorded by the platform",
      label: version.authorLabel ?? "Platform",
    };
  }
  return {
    Icon: User,
    tint: "bg-accent/10 text-accent",
    title: "Changed by a person",
    label: version.authorLabel ?? "Canvas",
  };
}
